import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { ShipmentForm, shipmentSchema } from "../../schemas/shipmentSchema";
import { Shipment, ShipmentMessageType } from "../../models/Shipment";
import { useEffect, useMemo, useState } from "react";
import { useApiProvider } from "../../providers/api/apiProvider";
import { deleteShipment, saveShipment, updateShipment } from "../../services/shipmentService";
import { IShowMessage } from "../../types/show";
import { useMultipartUpload } from "../../hooks/useMultipartUpload";
import { ShipmentMutationPayload } from "../../services/shipmentService";

export const useShipmentModalController = ( shipment: Shipment | null, open: boolean, onClose: () => void ) => {
  const {
    simpleApiCall,
  } = useApiProvider();
  const {
    state: multipartUploadState,
    multipartEnabled,
    ensureCompletedUpload,
    abortUpload,
    resetUpload,
  } = useMultipartUpload({
    scope: shipment?.id ? `shipment:${ shipment.id }` : "shipment:draft",
  });

  const form = useForm<ShipmentForm>({
    resolver: yupResolver( shipmentSchema ),
    defaultValues: {
      id: null,
      content: "",
      shipmentMessageType: ShipmentMessageType.TEXT,
      title: "",
    },
    mode: 'all',
  });
  const [ showMessage, setShowMessage ] = useState<IShowMessage>({
    delete: false,
    lock: false,
  });

  const toggleShowMessage = ( field: keyof IShowMessage ) => {
    setShowMessage(( value ) => {
      const toggle = !value[ field ];

      value.delete = false;
      value.lock = false;

      value[ field ] = toggle;

      return { ...value };
    });
  };

  const onSubmit = async ( data: ShipmentForm ) => {
    let fileMetadata = null;

    if( multipartEnabled && data.file ) {
      fileMetadata = await ensureCompletedUpload( data.file, {
        context: "shipment",
        ownerId: undefined,
        metadata: {
          shipmentId: data.id,
          contactId: data.contact.id,
          shipmentMessageType: data.shipmentMessageType,
        },
      });

      if( !fileMetadata ) {
        return;
      }
    }

    const payload: ShipmentMutationPayload = {
      ...data,
      fileMetadata,
      file: fileMetadata ? null : data.file,
    };

    const response = await simpleApiCall<ShipmentMutationPayload, boolean>( data.id ? updateShipment : saveShipment, payload );

    if( response !== null ) {
      resetUpload();
      onClose();
    }
  };

  const handleSubmit = form.handleSubmit( onSubmit );

  const handleDelete = async () => {
    const response = await simpleApiCall<string, boolean>( deleteShipment, shipment?.id ?? "" );

    if( response !== null ) onClose();
  };

  useEffect(() => {
    if( !open ) {
      form.reset();
      resetUpload();

      setShowMessage({
        delete: false,
        lock: false,
      });
    } else if( shipment !== null ) {
      form.setValue("id", shipment.id );
      form.setValue("title", shipment.title );
      form.setValue("content", shipment.content );
      form.setValue("contact.id", shipment.contact.id );
      form.setValue("sendDate", shipment.sendDate );
      form.setValue("sendTime", shipment.sendTime );
      form.setValue("shipmentMessageType", shipment.shipmentMessageType );
      form.setValue("url", shipment.url, {
        shouldValidate: true,
      });
    }
    // eslint-disable-next-line
  }, [ open ]);

  return useMemo(() => ({
    form,
    showMessage,
    multipartUploadState,
    multipartEnabled,
    handleSubmit,
    toggleShowMessage,
    handleDelete,
    abortUpload,
    // eslint-disable-next-line
  }), [
    abortUpload,
    form,
    multipartEnabled,
    multipartUploadState,
    showMessage,
  ]);
};
